import { useEffect, useRef, useState } from "react";
import { api } from "../auth/authClient";
import {
  TANKS,
  resolveSelection,
  type DecalStyle,
  type Loadout,
  type PublicProfile,
} from "@artillery/shared";
import type { Route } from "../router";
import { useAuth } from "../auth/AuthProvider";
import { loadSelection } from "../game/loadoutStorage";
import { renderLoadoutCanvas } from "../game/tankPreview";
import { RANKS, rankFor } from "../game/ranks";

interface Props {
  username: string;
  navigate: (r: Route) => void;
}

interface Medal {
  id: string;
  name: string;
  blurb: string;
  earned: boolean;
  iconUrl: string; // path to CC-BY icon in /public/icons/medals/
}

function medalsFor(p: PublicProfile): Medal[] {
  const winRate = p.wins + p.losses > 0 ? p.wins / (p.wins + p.losses) : 0;
  return [
    { id: "first",    name: "First Blood",     blurb: "Confirmed kill",            earned: p.kills >= 1,    iconUrl: "/icons/medals/crosshair.svg" },
    { id: "iron",     name: "Iron Fist",       blurb: "10 kills",                  earned: p.kills >= 10,   iconUrl: "/icons/medals/crossed-swords.svg" },
    { id: "apex",     name: "Apex Predator",   blurb: "50 kills",                  earned: p.kills >= 50,   iconUrl: "/icons/medals/medal-skull.svg" },
    { id: "veteran",  name: "Veteran",         blurb: "10 matches survived",       earned: p.matches >= 10, iconUrl: "/icons/medals/ribbon-medal.svg" },
    { id: "ace",      name: "Triple Ace",      blurb: "50 matches",                earned: p.matches >= 50, iconUrl: "/icons/medals/star-medal.svg" },
    { id: "win-rate", name: "Decorated",       blurb: "≥60% win rate · 10+ games", earned: winRate >= 0.6 && p.matches >= 10, iconUrl: "/icons/medals/medallist.svg" },
    { id: "top",      name: "Top of the List", blurb: "≥1600 MMR",                 earned: p.mmr >= 1600,   iconUrl: "/icons/medals/medal.svg" },
  ];
}

export function ProfilePage({ username, navigate }: Props): JSX.Element {
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const { session } = useAuth();
  const isSelf = session?.user.username === username;

  useEffect(() => {
    if (!username) return;
    setProfile(null);
    setError(null);
    setIsDemo(false);
    api.profile(username)
      .then((r) => setProfile(r.profile))
      .catch((err) => {
        // Operators shown on the padded leaderboard don't exist in the DB.
        // Rather than 404-ing, synthesize a profile from the username hash
        // so the card still renders with tank + stats for design preview.
        const msg = String(err?.message ?? "").toLowerCase();
        if (msg.includes("not found") || msg.includes("404")) {
          setProfile(syntheticProfile(username));
          setIsDemo(true);
        } else {
          setError(err?.message ?? "Failed to load");
        }
      });
  }, [username]);

  if (!username) {
    return (
      <div className="profile-desk">
        <div className="folder">
          <div className="folder-tab">No file</div>
          <div className="folder-section">
            <h2 className="folder-section-title">§ NO PROFILE SELECTED</h2>
            <button className="paper-btn" onClick={() => navigate({ name: "leaderboard" })}>
              Browse leaderboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) return <div className="profile-desk"><div className="folder"><div className="folder-section"><div className="error">{error}</div></div></div></div>;
  if (!profile) return <div className="profile-desk"><div className="folder"><div className="folder-section"><p style={{ color: "var(--paper-ink-dim)" }}>Loading service record…</p></div></div></div>;

  const rank = rankFor(profile.mmr);
  const toNext = Math.max(0, rank.next - profile.mmr);
  const totalGames = profile.wins + profile.losses;
  const winRate = totalGames > 0 ? profile.wins / totalGames : 0;
  const kd = profile.deaths > 0 ? profile.kills / profile.deaths : profile.kills;
  const medals = medalsFor(profile);

  return (
    <div className="profile-desk">
      <div className="folder">
        <div className="folder-left">
          <div className="folder-tab">{profile.username}</div>
          <div className="folder-casenumber">
            F-{caseNumberFor(profile.id)}
          </div>
          <div className="folder-photo">
            <span className="folder-tape" aria-hidden />
            <div className="folder-photo-frame">
              {isSelf ? <SelfVehicleCard /> : <OperatorVehicleCard username={profile.username} />}
            </div>
          </div>

          <div className="taped-ribbons">
            {medals.filter((m) => m.earned).map((m, i) => (
              <div
                key={m.id}
                className="taped-ribbon"
                style={{ transform: `rotate(${(i * 137) % 7 - 3}deg)` }}
                title={`${m.name} — ${m.blurb}`}
              >
                <span className="taped-ribbon-tape" aria-hidden />
                <span
                  className="taped-ribbon-icon icon-mask"
                  style={{
                    background: "var(--brass-bright)",
                    WebkitMaskImage: `url(${m.iconUrl})`,
                    maskImage: `url(${m.iconUrl})`,
                  }}
                />
                <span className="taped-ribbon-name">{m.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="folder-right">
          <div className="paper-sheet">
            <span className="paper-paperclip" aria-hidden />
            <div className="folder-stamp">{isDemo ? "DEMO" : "CLASSIFIED"}</div>

            <div className="folder-section folder-header">
              <div className="folder-letterhead">
                <div className="letterhead-line">SERVICE RECORD · SN-{profile.id.slice(0, 8).toUpperCase()}</div>
                <h1 className="letterhead-name">{profile.username}</h1>
                <div className="letterhead-rank">
                  <span className="rank-chip" style={{ borderColor: rank.color, color: rank.color }}>
                    <span
                      className="rank-icon icon-mask"
                      style={{
                        background: rank.color,
                        WebkitMaskImage: `url(${rank.icon})`,
                        maskImage: `url(${rank.icon})`,
                      }}
                    />
                    {rank.name}
                  </span>
                  <span className="rank-mmr">{profile.mmr}<span className="rank-mmr-lbl">MMR</span></span>
                </div>
                <div className="rank-status">
                  {toNext > 0
                    ? `Status — ${toNext} MMR to ${RANKS[Math.min(RANKS.length - 1, RANKS.indexOf(rank) + 1)]!.name}`
                    : "Status — at max rank"}
                </div>
              </div>
            </div>

            <div className="folder-section">
              <div className="folder-section-title">§ COMBAT RECORD</div>
              <div className="combat-grid">
                <CombatTile big label="Matches" value={profile.matches} />
                <CombatTile big label="Win Rate" value={`${Math.round(winRate * 100)}%`} accent={winRate >= 0.5 ? "ok" : "warn"} />
                <CombatTile big label="Kills" value={profile.kills} accent="amber" />
                <CombatTile big label="K / D" value={kd.toFixed(2)} accent={kd >= 1 ? "ok" : "bad"} />
                <CombatTile label="Wins" value={profile.wins} />
                <CombatTile label="Losses" value={profile.losses} />
                <CombatTile label="Deaths" value={profile.deaths} />
                <CombatTile label="Enlisted" value={new Date(profile.createdAt).toLocaleDateString()} />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// Deterministic pseudo-loadout derived from a username — same technique
// the leaderboard uses so every operator has a distinct silhouette even
// when the server doesn't persist public loadouts yet.
/** Build a fake PublicProfile from a username hash so the leaderboard's
 *  synthetic roster slots still open a viewable profile instead of
 *  dead-ending on a 404. Stats, MMR, matches etc. all vary per username
 *  so the preview looks alive. */
function syntheticProfile(username: string): PublicProfile {
  const h = opHash(username);
  const mmr = 900 + (h % 1800);
  const matches = 50 + ((h >> 3) % 260);
  const wins = Math.round(matches * (0.3 + ((h >> 6) & 0x1f) / 80));
  const losses = Math.max(0, matches - wins);
  const kills = 80 + ((h >> 9) % 320);
  const deaths = Math.max(1, ((h >> 12) % 200) + 10);
  return {
    id: `demo-${(h >>> 0).toString(16)}`,
    username,
    mmr,
    wins,
    losses,
    kills,
    deaths,
    matches,
    createdAt: new Date(Date.now() - ((h >> 15) % 365) * 86400000).toISOString(),
  };
}

function opHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return Math.abs(h);
}

function caseNumberFor(id: string): string {
  const h = opHash(id);
  const year = 2024 + (h % 3);
  const seq = ((h >> 5) % 9000) + 1000;
  return `${year}-${seq}`;
}

const OP_DECALS: DecalStyle[] = [
  "none", "star", "skull", "crosshair", "cross", "flame", "shield",
];

function loadoutFromUsername(username: string): Loadout {
  const h = opHash(username);
  // Pick a tank deterministically so the same operator always shows the
  // same hull, then layer a deterministic decal on top of the tank's
  // factory paint job.
  const tank = TANKS[h % TANKS.length]!;
  const decal = OP_DECALS[(h >> 18) % OP_DECALS.length]!;
  return resolveSelection({ tankSku: tank.sku, decal });
}

function OperatorVehicleCard({ username }: { username: string }): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  const W = 360, H = 220;
  const l = loadoutFromUsername(username);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(W * dpr);
    c.height = Math.round(H * dpr);
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawProfileTank(ctx, W, H, l);
    // `l` is derived from `username` — one dep is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);
  return (
    <div className="self-vehicle">
      <canvas ref={ref} className="tank-plate big" style={{ width: W, height: H }} />
      <div className="self-vehicle-spec">
        <div><strong>HULL</strong> {l.body}</div>
        <div><strong>TURRET</strong> {l.turret}</div>
        <div><strong>BARREL</strong> {l.barrel}</div>
      </div>
    </div>
  );
}

function SelfVehicleCard(): JSX.Element {
  const l = resolveSelection(loadSelection());
  const ref = useRef<HTMLCanvasElement>(null);
  const W = 360, H = 220;
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(W * dpr);
    c.height = Math.round(H * dpr);
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawProfileTank(ctx, W, H, l);
    // Fine-grained deps — listing l whole churns on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [l.body, l.turret, l.barrel, l.primaryColor, l.accentColor]);
  return (
    <div className="self-vehicle">
      <canvas ref={ref} className="tank-plate big" style={{ width: W, height: H }} />
      <div className="self-vehicle-spec">
        <div><strong>HULL</strong> {l.body}</div>
        <div><strong>TURRET</strong> {l.turret}</div>
        <div><strong>BARREL</strong> {l.barrel}</div>
      </div>
    </div>
  );
}

function drawProfileTank(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  l: Loadout,
): void {
  renderLoadoutCanvas(ctx, {
    width: w,
    height: h,
    bodyStyle: l.body,
    turretStyle: l.turret,
    barrelStyle: l.barrel,
    primary: `#${l.primaryColor.toString(16).padStart(6, "0")}`,
    accent:  `#${l.accentColor.toString(16).padStart(6, "0")}`,
    pattern: l.pattern,
    patternColor: `#${l.patternColor.toString(16).padStart(6, "0")}`,
    decal: l.decal,
    showDeck: true,
  });
}

function CombatTile({
  label, value, accent, big,
}: {
  label: string;
  value: string | number;
  accent?: "ok" | "bad" | "warn" | "amber";
  big?: boolean;
}) {
  const color =
    accent === "ok" ? "var(--ok)"
    : accent === "bad" ? "var(--danger)"
    : accent === "warn" ? "var(--warn)"
    : accent === "amber" ? "var(--theme-accent-bright)"
    : "var(--ink)";
  return (
    <div className={`combat-tile ${big ? "big" : ""}`}>
      <div className="combat-label">{label}</div>
      <div className="combat-value" style={{ color }}>{value}</div>
    </div>
  );
}
